import { useEffect, useMemo, useState } from 'react';
import { useAccountApi } from '../../api/AccountApiProvider';
import { USING_STUBS } from '../../api';
import { useAsync } from '../../api/useAsync';
import { watchRollout } from '../../api/watch';
import { toApiError } from '../../api/errors';
import type { ApiError } from '../../api/errors';
import type {
  ComponentPair,
  ComponentVersion,
  Rollout,
  RolloutBoard,
  RolloutWave,
  TenantVersions,
} from '../../api/types';
import { ROLLOUT_WAVES } from '../../api/types';
import { useAccountSession } from '../../auth/AccountSessionProvider';
import { mayRollOutVersion } from '../../auth/capabilities';
import { useI18n } from '../../i18n/useI18n';
import { Link } from '../../router/Router';
import { Alert, ErrorAlert } from '../../ui/Alert';
import { DataTable } from '../../ui/DataTable';
import type { Column } from '../../ui/DataTable';
import { FilterBar, SearchInput, SegmentedFilter } from '../../ui/form';
import { Button, Card, Chip, Loading, PageHead, Stat } from '../../ui/primitives';
import { Modal } from '../../ui/Modal';
import { StatusBadge, TenantStateBadge } from '../../ui/StatusBadge';
import { StubRolloutHint } from '../../stubs/StubBanner';
import { RolloutLine } from './RolloutLine';
import { StartRolloutDialog } from './StartRolloutDialog';
import { SignOffWaveDialog } from './SignOffWaveDialog';
import { RetryMovesDialog } from './RetryMovesDialog';
import { StopRolloutDialog } from './StopRolloutDialog';
import {
  advanceOffer,
  brokenPairs,
  componentOf,
  fleetTally,
  knownWave,
  mayStart,
  movingNow,
  readingOf,
  retryOffer,
  stepLabelKey,
  stepTally,
  stepTone,
  stillGoing,
  verdictLabelKey,
  verdictMeaningKey,
  verdictRank,
  verdictTone,
  waveLabelKey,
  waveOrder,
  worstVerdict,
} from './rollout';

/**
 * What every tenancy's deployment is running, and moving the fleet off it.
 *
 * A board, in the order somebody reads one: what may be done, what is happening,
 * how much of it there is, and then the rows.
 *
 * **What was read and what was typed are never the same column.** A tenancy's
 * declared tag is what somebody entered — it says what the next start of a
 * container will pull, and a mutable tag says something different tomorrow. The
 * versions beside it are what components answered. They are labelled apart, and
 * nothing here fills a gap in the second with a value from the first.
 *
 * **A version that is not known is drawn as not known, with the reason.** Two
 * of the four components cannot be read at all — nothing a connector sends carries
 * a version, and a client enrols nothing — so neither has a column: four grey
 * cells across the whole fleet are not data. They are inside the row that opens,
 * and that the two can never be read is said once, under the table.
 *
 * **Moving the fleet is an act with consequences, not a switch.** Every button
 * that moves anything opens a dialog that says what will move, in what order,
 * and what has moved already; and every one of them is behind
 * `roll_out_version`, which the provider's operator alone carries. Nobody
 * without it reaches this screen at all — it is not on their menu and not among
 * their addresses — so there is no read-only notice to write.
 *
 * **A rollout that ended still has something to say.** It stays here as one
 * line drawn as ended: which tenancies it moved and which it did not is why part
 * of the fleet is on the new version. What may be done to it follows from `live`
 * and from nothing else — an ended one can be neither signed off, moved again
 * nor stopped, and it is no reason not to start another.
 */
export function RolloutScreen() {
  const { t } = useI18n();
  const api = useAccountApi();
  const { session } = useAccountSession();

  const mayMove = mayRollOutVersion(session);

  const state = useAsync((signal) => api.provider.readRollout(signal), 'rollout');
  /** An answer newer than the one the read produced: every call answers the whole board. */
  const [fresher, setFresher] = useState<RolloutBoard | null>(null);
  const [dialog, setDialog] = useState<Dialog>(null);

  const board = fresher ?? (state.status === 'ready' ? state.value : null);
  const moving = board !== null && movingNow(board);

  // While a wave is being moved the board changes without anybody touching it,
  // so it re-reads itself — and only then. The loop is `watchRollout`, beside
  // the other one this console runs, because polling here is part of the
  // contract rather than a habit of the screen: starting a rollout and moving
  // its stragglers both answer `202` with the board as it was when the wave
  // began moving.
  useEffect(() => {
    if (!moving) return undefined;
    const stop = new AbortController();
    void watchRollout((signal) => api.provider.readRollout(signal), setFresher, {
      signal: stop.signal,
      stillMoving: movingNow,
    });
    return () => {
      stop.abort();
    };
  }, [moving, api]);

  if (state.status === 'loading' && board === null) {
    return (
      <div className="content-inner">
        <Loading />
      </div>
    );
  }

  if (state.status === 'error' && board === null) {
    return (
      <div className="content-inner">
        <ErrorAlert
          error={state.error}
          action={
            <div style={{ marginTop: 12 }}>
              <Button size="sm" onClick={state.reload}>
                {t('common.retry')}
              </Button>
            </div>
          }
        />
      </div>
    );
  }

  if (board === null) return null;

  const rollout = board.rollout;
  const live = rollout !== undefined && stillGoing(rollout);

  return (
    <div className="content-inner">
      <PageHead
        title={t('rollout.title')}
        {...(mayMove
          ? {
              actions: (
                <Actions
                  board={board}
                  rollout={rollout}
                  onDialog={(next) => {
                    setDialog(next);
                  }}
                />
              ),
            }
          : {})}
      />

      <div className="stack">
        {USING_STUBS && (
          <StubRolloutHint
            onChanged={() => {
              setFresher(null);
              state.reload();
            }}
          />
        )}

        <RolloutLine rollout={rollout} moving={moving} detail />

        {rollout === undefined ? (
          <FleetTiles board={board} />
        ) : (
          <MoveTiles
            board={board}
            rollout={rollout}
            onForecast={() => {
              setDialog('forecast');
            }}
          />
        )}

        <BrokenPairs board={board} />

        <Tenancies
          board={board}
          mayMove={mayMove}
          hasRollout={rollout !== undefined}
          live={live}
          onBoard={setFresher}
        />
      </div>

      {dialog === 'forecast' && rollout !== undefined && (
        <ForecastDialog
          rollout={rollout}
          onClose={() => {
            setDialog(null);
          }}
        />
      )}

      {dialog === 'start' && mayStart(board) && (
        <StartRolloutDialog
          board={board}
          actorName={session?.actor.audit_name ?? ''}
          onClose={() => { setDialog(null); }}
          onStarted={(next) => {
            setFresher(next);
            setDialog(null);
          }}
        />
      )}

      {/* The three that act on a rollout are shut behind `live` here as well as
          at the button: a board that refreshed itself into an ended state with
          a dialog open would otherwise leave a live-looking one on screen. */}
      {dialog === 'signOff' && rollout !== undefined && live && (
        <SignOffWaveDialog
          board={board}
          rollout={rollout}
          actorName={session?.actor.audit_name ?? ''}
          onClose={() => { setDialog(null); }}
          onSigned={(next) => {
            setFresher(next);
            setDialog(null);
          }}
        />
      )}

      {dialog === 'retry' && rollout !== undefined && live && (
        <RetryMovesDialog
          rollout={rollout}
          actorName={session?.actor.audit_name ?? ''}
          onClose={() => { setDialog(null); }}
          onRetried={(next) => {
            setFresher(next);
            setDialog(null);
          }}
        />
      )}

      {dialog === 'stop' && rollout !== undefined && live && (
        <StopRolloutDialog
          rollout={rollout}
          onClose={() => { setDialog(null); }}
          onStopped={(next) => {
            setFresher(next);
            setDialog(null);
          }}
        />
      )}
    </div>
  );
}

type Dialog = 'start' | 'signOff' | 'retry' | 'stop' | 'forecast' | null;

/**
 * Everything that moves anything, on the first screen.
 *
 * Which of them stand follows from `live` and from the server's own two offers,
 * and a refusal to sign off is shown where the press would have been — on the
 * button that is out — rather than as a band across the page. The sentence is
 * the server's either way: the dialog carries it in full.
 */
function Actions({
  board,
  rollout,
  onDialog,
}: {
  board: RolloutBoard;
  rollout: Rollout | undefined;
  onDialog: (dialog: Dialog) => void;
}) {
  const { t } = useI18n();

  const live = rollout !== undefined && stillGoing(rollout);

  if (!live) {
    return (
      <Button
        variant="primary"
        disabled={!mayStart(board)}
        onClick={() => {
          onDialog('start');
        }}
      >
        {t('rollout.start')}
      </Button>
    );
  }

  const offer = advanceOffer(rollout);
  const again = retryOffer(rollout);
  const refusal = offer.offered
    ? undefined
    : (offer.blockedBy ?? t('rollout.advance.blocked.noReason'));

  return (
    <>
      <span {...(refusal === undefined ? {} : { title: refusal })}>
        <Button
          variant="primary"
          disabled={!offer.offered}
          onClick={() => {
            onDialog('signOff');
          }}
        >
          {t('rollout.advance.submit')}
        </Button>
      </span>
      {/* Shown when the server says there is something to move again, and only
          then: a button standing on every healthy wave would be one that does
          nothing on almost every wave it appears on. */}
      {again.offered && (
        <Button
          onClick={() => {
            onDialog('retry');
          }}
        >
          {t('rollout.retry.submit')}
        </Button>
      )}
      <Button
        onClick={() => {
          onDialog('stop');
        }}
      >
        {t('rollout.stop')}
      </Button>
    </>
  );
}

/**
 * The tenancies whose own two components were seen running versions with a
 * named break between them.
 *
 * One band for the fleet rather than one on every row: it is a state that is
 * wrong now — it stops the wave those tenancies are in from being signed off —
 * and the rows it names are where somebody goes.
 */
function BrokenPairs({ board }: { board: RolloutBoard }) {
  const { t } = useI18n();

  const broken = board.tenants.filter((row) => brokenPairs(row).length > 0);
  if (broken.length === 0) return null;

  return (
    <Alert tone="deny" title={t('rollout.broken.title')}>
      <p>{t('rollout.broken.body')}</p>
      <div className="row" style={{ marginTop: 8 }}>
        {broken.map((row) => (
          <Chip key={row.slug}>{row.slug}</Chip>
        ))}
      </div>
    </Alert>
  );
}

/** How far the rollout on record got, counted over the rows already in hand. */
function MoveTiles({
  board,
  rollout,
  onForecast,
}: {
  board: RolloutBoard;
  rollout: Rollout;
  onForecast: () => void;
}) {
  const { t, formatNumber } = useI18n();

  const tally = stepTally(board);
  const unavailable = rollout.forecast_unavailable;

  return (
    <div className="grid g-4">
      <Stat label={t('rollout.tally.moved')} value={formatNumber(tally.moved)} />
      <Stat
        label={t('rollout.tally.failed')}
        value={formatNumber(tally.failed)}
        {...(tally.failed > 0 ? { tone: 'deny' as const } : {})}
      />
      <Stat label={t('rollout.tally.pending')} value={formatNumber(tally.pending)} />
      {unavailable === undefined ? (
        <Stat
          label={t('rollout.tally.crosses')}
          value={formatNumber(rollout.crosses.length)}
          {...(rollout.crosses.length > 0 ? { tone: 'warn' as const } : {})}
          onClick={onForecast}
        />
      ) : (
        <Stat
          label={t('rollout.tally.crosses')}
          absent={{ label: t('rollout.tally.notWorkedOut'), reason: unavailable }}
        />
      )}
    </div>
  );
}

/** What the fleet is, when no rollout is on record to measure it against. */
function FleetTiles({ board }: { board: RolloutBoard }) {
  const { t, formatNumber } = useI18n();

  const tally = fleetTally(board);

  return (
    <div className="grid g-4">
      <Stat label={t('rollout.tally.tenants')} value={formatNumber(tally.tenants)} />
      <Stat label={t('rollout.tally.declared')} value={formatNumber(tally.declared)} />
      <Stat
        label={t('rollout.tally.broken')}
        value={formatNumber(tally.broken)}
        {...(tally.broken > 0 ? { tone: 'deny' as const } : {})}
      />
      <Stat label={t('rollout.tally.notMovable')} value={formatNumber(tally.notMovable)} />
    </div>
  );
}

/** What the move crosses, on the tile that counts it. */
function ForecastDialog({ rollout, onClose }: { rollout: Rollout; onClose: () => void }) {
  const { t } = useI18n();

  return (
    <Modal
      title={t('rollout.forecast.title')}
      subtitle={t('rollout.forecast.body')}
      onClose={onClose}
      footer={
        <Button variant="primary" onClick={onClose}>
          {t('common.close')}
        </Button>
      }
    >
      {rollout.crosses.length === 0 ? (
        <p className="muted">{t('rollout.forecast.none')}</p>
      ) : (
        <div className="stack">
          {rollout.crosses.map((cross) => (
            <div key={`${cross.release}:${cross.named_in}`}>
              <p>
                <span className="mono">{cross.release}</span>
                {' · '}
                {cross.between.map((pair) => pairName(pair)).join(', ')}
              </p>
              <p className="muted">{cross.what}</p>
              <p className="hint">
                {t('rollout.namedIn')}
                <span className="mono"> {cross.named_in}</span>
              </p>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

type WaveFilter = string;
type StepFilter = 'all' | 'moved' | 'failed' | 'pending';

/**
 * The fleet, a row to a tenancy.
 *
 * The wave counters live in the filter's own buttons, which is what a table of
 * two rows headed "Waves" used to be. They are the server's arithmetic — how
 * many visible, movable tenancies each wave holds — and the only figure on this
 * screen this console did not work out itself.
 */
function Tenancies({
  board,
  mayMove,
  hasRollout,
  live,
  onBoard,
}: {
  board: RolloutBoard;
  mayMove: boolean;
  hasRollout: boolean;
  live: boolean;
  onBoard: (board: RolloutBoard) => void;
}) {
  const { t, formatDate } = useI18n();

  const [wave, setWave] = useState<WaveFilter>('all');
  const [step, setStep] = useState<StepFilter>('all');
  const [query, setQuery] = useState('');

  const waves = waveOrder(board);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return board.tenants.filter((row) => {
      if (wave !== 'all' && row.wave !== wave) return false;
      if (step !== 'all' && row.step?.outcome !== step) return false;
      if (needle !== '' && !row.slug.toLowerCase().includes(needle)) return false;
      return true;
    });
  }, [board, wave, step, query]);

  const columns: Column<TenantVersions>[] = [
    {
      key: 'slug',
      header: t('rollout.col.tenant'),
      sort: (a, b) => a.slug.localeCompare(b.slug),
      cell: (row) => (
        <Link to={{ name: 'account.tenant', slug: row.slug }} className="row-link">
          <span className="t-name mono">{row.slug}</span>
        </Link>
      ),
    },
    {
      key: 'state',
      header: t('rollout.col.state'),
      sort: (a, b) => a.state.localeCompare(b.state),
      cell: (row) => <TenantStateBadge state={row.state} />,
    },
    {
      key: 'wave',
      header: t('rollout.col.wave'),
      sort: (a, b) => a.wave.localeCompare(b.wave),
      cell: (row) =>
        mayMove ? (
          <WaveCell key={`${row.slug}:${row.wave}`} row={row} locked={live} onBoard={onBoard} />
        ) : (
          <Chip>{t(waveLabelKey(row.wave))}</Chip>
        ),
    },
    {
      key: 'declared',
      header: t('rollout.col.declared'),
      mono: true,
      sort: (a, b) => a.declared.localeCompare(b.declared),
      cell: (row) => row.declared,
    },
    {
      key: 'authority',
      header: t('rollout.col.authority'),
      mono: true,
      sort: (a, b) => versionOrder(a, 'authority').localeCompare(versionOrder(b, 'authority')),
      cell: (row) => <VersionCell component={componentOf(row, 'authority')} />,
    },
    {
      key: 'proxy',
      header: t('rollout.col.proxy'),
      mono: true,
      sort: (a, b) => versionOrder(a, 'proxy').localeCompare(versionOrder(b, 'proxy')),
      cell: (row) => <VersionCell component={componentOf(row, 'proxy')} />,
    },
    {
      key: 'pairs',
      header: t('rollout.col.pairs'),
      sort: (a, b) => verdictRank(worstVerdict(a) ?? '') - verdictRank(worstVerdict(b) ?? ''),
      cell: (row) => <VerdictCell row={row} />,
    },
    {
      key: 'observed',
      header: t('rollout.col.observed'),
      hideSm: true,
      sort: (a, b) => (a.observed_at ?? '').localeCompare(b.observed_at ?? ''),
      cell: (row) =>
        row.observed_at === undefined ? (
          <StatusBadge
            tone="neutral"
            label={t('rollout.observed.never')}
            hint={t('rollout.tenant.observed.never')}
          />
        ) : (
          formatDate(row.observed_at)
        ),
    },
    {
      key: 'step',
      header: t('rollout.col.step'),
      sort: (a, b) => (a.step?.outcome ?? '').localeCompare(b.step?.outcome ?? ''),
      cell: (row) => <StepCell row={row} />,
    },
  ];

  return (
    <>
      <FilterBar
        filters={
          <>
            <SegmentedFilter<WaveFilter>
              legend={t('rollout.filter.wave')}
              value={wave}
              onChange={setWave}
              options={[
                {
                  value: 'all',
                  label: t('rollout.filter.wave.all'),
                  count: waves.reduce((sum, one) => sum + one.tenants, 0),
                },
                ...waves.map((one) => ({
                  value: one.wave,
                  label: t(waveLabelKey(one.wave)),
                  count: one.tenants,
                  marker: one.under_way,
                })),
              ]}
            />
            {hasRollout && (
              <SegmentedFilter<StepFilter>
                legend={t('rollout.filter.step')}
                value={step}
                onChange={setStep}
                options={[
                  { value: 'all', label: t('rollout.filter.step.all') },
                  { value: 'moved', label: t('rollout.move.moved') },
                  { value: 'failed', label: t('rollout.move.failed') },
                  { value: 'pending', label: t('rollout.move.pending') },
                ]}
              />
            )}
          </>
        }
        search={
          <SearchInput
            label={t('rollout.search.label')}
            placeholder={t('rollout.search.placeholder')}
            value={query}
            onChange={setQuery}
          />
        }
      />

      {/* Once, above the table, rather than on every row: it is one fact about
          the fleet, and the same sentence a stale board is refused with. */}
      {mayMove && live && <p className="hint">{t('rollout.wave.underWay')}</p>}

      <Card>
        <DataTable<TenantVersions>
          caption={t('rollout.title')}
          columns={columns}
          rows={visible}
          total={board.tenants.length}
          rowKey={(row) => row.slug}
          initialSort={{ key: 'slug', direction: 'asc' }}
          renderExpanded={(row) => <TenancyDetail row={row} />}
          empty={{ title: t('rollout.tenants.none.title'), body: t('rollout.tenants.none.body') }}
          noMatch={{ title: t('tenants.empty.title'), body: t('tenants.empty.body') }}
        />
      </Card>
    </>
  );
}

/**
 * One component's version in a column, or the absence of one.
 *
 * Never a dash: a reading that failed and a component nothing can ask are two
 * different silences, and the badge says which. What the reason was is in the
 * row that opens.
 */
function VersionCell({ component }: { component: ComponentVersion | undefined }) {
  const { t } = useI18n();

  if (component === undefined) {
    return <StatusBadge tone="neutral" label={t('rollout.component.unreadable')} />;
  }
  const reading = readingOf(component);
  if (reading.read) return <>{reading.version}</>;
  return (
    <StatusBadge
      tone="neutral"
      label={t(reading.readBy === undefined ? 'rollout.component.unreadable' : 'rollout.component.notRead')}
      {...(reading.reason === undefined ? {} : { hint: reading.reason })}
    />
  );
}

/** The worst of a row's four verdicts, with what it means on the badge. */
function VerdictCell({ row }: { row: TenantVersions }) {
  const { t } = useI18n();

  const worst = worstVerdict(row);
  if (worst === undefined) return <span className="faint">{t('common.dash')}</span>;
  return (
    <StatusBadge
      tone={verdictTone(worst)}
      label={t(verdictLabelKey(worst))}
      hint={t(verdictMeaningKey(worst))}
    />
  );
}

/** What the rollout on record did to this tenancy, and what it came from. */
function StepCell({ row }: { row: TenantVersions }) {
  const { t } = useI18n();

  const step = row.step;
  if (step === undefined) return <span className="faint">{t('common.dash')}</span>;

  return (
    <span className="cell-flex">
      <StatusBadge tone={stepTone(step)} label={t(stepLabelKey(step))} />
      <span className="t-sub mono">
        {t('rollout.move.from')} {step.from}
      </span>
    </span>
  );
}

/**
 * Which wave this tenancy moves in: a choice and then a press.
 *
 * Never a list that acts on change. It decides who finds out about a new
 * version first, and a control that acted the moment a list scrolled under
 * somebody's cursor would decide that by accident.
 *
 * Out while a rollout is going, because the server refuses it then: a wave's
 * tenancies are read when the wave starts moving, so a tenancy moved between
 * waves halfway through is one the rollout has stopped counting. The refusal is
 * still handled — this console is not the only way in, and a board read a
 * moment before somebody else started a rollout would offer the press.
 */
function WaveCell({
  row,
  locked,
  onBoard,
}: {
  row: TenantVersions;
  locked: boolean;
  onBoard: (board: RolloutBoard) => void;
}) {
  const { t } = useI18n();
  const api = useAccountApi();

  const [wave, setWave] = useState<string>(row.wave);
  const [failure, setFailure] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);

  const chosen = knownWave(wave);
  const changed = chosen !== null && chosen !== row.wave;

  async function put(): Promise<void> {
    if (chosen === null) return;
    setFailure(null);
    setBusy(true);
    try {
      onBoard(await api.provider.setWave(row.slug, chosen));
    } catch (error: unknown) {
      setFailure(toApiError(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <span className="cell-flex">
      <span className="row">
        <select
          className="cell-select"
          aria-label={t('rollout.wave.choose')}
          value={wave}
          disabled={busy || locked}
          onChange={(event) => {
            setWave(event.target.value);
          }}
        >
          {ROLLOUT_WAVES.map((option: RolloutWave) => (
            <option key={option} value={option}>
              {t(waveLabelKey(option))}
            </option>
          ))}
        </select>
        {changed && !locked && (
          <button type="button" className="text-action" disabled={busy} onClick={() => void put()}>
            {busy ? `${t('rollout.wave.putting')}…` : t('rollout.wave.put')}
          </button>
        )}
      </span>
      {/* A conflict on this route is one thing — a rollout started while this
          board was on screen — so it is answered with the sentence that names
          the way through rather than with the fact that nothing happened. */}
      {failure !== null && (
        <span className="t-sub">
          {t(failure.code === 'conflict' ? 'rollout.wave.underWay' : 'rollout.wave.refused')}
        </span>
      )}
    </span>
  );
}

/**
 * The rest of one row: what each of its four components was seen running, how
 * its four pairs were judged, and what the last move did to it.
 *
 * Under the row rather than over the table. This screen is a comparison, and a
 * panel laid over it takes the comparison away at the moment somebody wanted
 * one row explained.
 */
function TenancyDetail({ row }: { row: TenantVersions }) {
  const { t, formatDateTime, plural } = useI18n();

  const step = row.step;

  return (
    <div className="stack">
      <div className="grid g-4">
        {row.components.map((component) => (
          <ComponentTile key={component.component} component={component} />
        ))}
      </div>

      <div className="stack">
        {row.pairs.map((pair) => (
          <PairLine key={pair.between.join(' ')} pair={pair} />
        ))}
      </div>

      {step !== undefined && (
        <div>
          <p className="muted">{t('rollout.move.title')}</p>
          <div className="row" style={{ marginTop: 8 }}>
            <StatusBadge tone={stepTone(step)} label={t(stepLabelKey(step))} />
            <span className="faint">
              {t('rollout.move.from')}
              <span className="mono"> {step.from}</span>
            </span>
            <span className="faint">{formatDateTime(step.changed_at)}</span>
          </div>
          {step.detail !== '' && <p className="muted">{step.detail}</p>}
          <p className="hint" style={{ marginTop: 6 }}>
            {plural('rollout.move.attempts', step.attempts)}
            {step.failures > 0 && ` · ${plural('rollout.move.failures', step.failures)}`}
          </p>
        </div>
      )}

      {!row.movable && <p className="hint">{t('rollout.tenant.notMovable')}</p>}

      <div className="row">
        <Link to={{ name: 'account.tenant', slug: row.slug }} className="btn btn-ghost btn-sm">
          {t('rollout.tenant.open')}
        </Link>
      </div>
    </div>
  );
}

/**
 * One component, and what it is running — or the absence of that, with the
 * reason and with what kind of absence it is.
 *
 * Three shapes and no dash anywhere: read, not read this time, and cannot be
 * read at all. The last two look nothing like a version, and they do not look
 * like each other either — one is a reading somebody can take again, the other
 * is a fact about gdsgate.
 */
function ComponentTile({ component }: { component: ComponentVersion }) {
  const { t } = useI18n();
  const reading = readingOf(component);

  if (reading.read) {
    return (
      <Stat
        label={component.component}
        value={<span className="mono">{reading.version}</span>}
        {...(reading.readBy === undefined ? {} : { sub: reading.readBy })}
      />
    );
  }

  return (
    <Stat
      label={component.component}
      absent={{
        label: t(
          reading.readBy === undefined ? 'rollout.component.unreadable' : 'rollout.component.notRead',
        ),
        ...(reading.reason === undefined ? {} : { reason: reading.reason }),
      }}
      {...(reading.readBy === undefined ? {} : { sub: reading.readBy })}
    />
  );
}

/**
 * One pair, judged. Four verdicts, and only one of them is red.
 *
 * What each verdict *means* is on the badge and not in a paragraph beside it:
 * four paragraphs a row times a fleet is the wall this board used to be.
 */
function PairLine({ pair }: { pair: ComponentPair }) {
  const { t } = useI18n();

  return (
    <div>
      <div className="row">
        <span className="mono">{pairName(pair.between)}</span>
        <StatusBadge
          tone={verdictTone(pair.verdict)}
          label={t(verdictLabelKey(pair.verdict))}
          hint={t(verdictMeaningKey(pair.verdict))}
        />
      </div>
      {pair.detail !== undefined && <p className="muted">{pair.detail}</p>}
      {pair.named_in !== undefined && pair.named_in.length > 0 && (
        <p className="hint">
          {t('rollout.namedIn')}
          {pair.named_in.map((where) => (
            <span key={where} className="mono">
              {' '}
              {where}
            </span>
          ))}
        </p>
      )}
    </div>
  );
}


/** What a column sorts a version by, with both kinds of absence last. */
function versionOrder(row: TenantVersions, component: string): string {
  const found = componentOf(row, component);
  if (found === undefined) return '￿';
  const reading = readingOf(found);
  return reading.read ? reading.version : '￿';
}

/** Two component names, as the board writes a pair. Punctuation, not prose. */
function pairName(between: [string, string]): string {
  return `${between[0]} ↔ ${between[1]}`;
}
